import { useFormContext } from "react-hook-form";

import { ErrorHint } from "src/base-components/ErrorHint";
import { Icon } from "src/base-components/Icon";
import { Input } from "src/base-components/Input";
import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import { getEventIcon } from "src/eventsCatalogue/utils";
import { EventSchemaFormData } from "src/schema/schemaEditor/types";

interface EventConfigurationRootProps {
  isLoading: boolean;
}

export const EventConfigurationRoot: React.FC<EventConfigurationRootProps> = ({
  isLoading,
}) => {
  const form = useFormContext<EventSchemaFormData>();

  const formValues = form.getValues();

  return (
    <div className="flex gap-x-2">
      <div className="flex h-15 w-15 shrink-0 items-center justify-center rounded-xl border border-gray-200">
        {isLoading ? (
          <SkeletonPlaceholder height="h-6" width="w-6" />
        ) : (
          <Icon
            color="text-gray-600"
            icon={getEventIcon({
              event_type: formValues.event_type,
              internal_name: formValues.internal_name,
              display_name_singular: formValues.display_name_singular,
              display_name_plural: formValues.display_name_plural,
              display_symbol: formValues.display_symbol,
              properties: formValues.properties,
              entities_json_schema: formValues.entities_json_schema,
              json_schema: formValues.json_schema,
            })}
            size="2xl"
          />
        )}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-y-2">
        <div className="text-gray-500 font-inter-normal-12px">Name</div>
        {isLoading ? (
          <SkeletonPlaceholder
            height="h-8"
            rounded="rounded-lg"
            width="w-full"
          />
        ) : (
          <Input
            disabled
            fullWidth
            monospaced
            {...form.register("event_type", { required: true })}
          />
        )}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-y-2">
        <div className="text-gray-500 font-inter-normal-12px">
          Display Name Singular
        </div>
        {isLoading ? (
          <SkeletonPlaceholder
            height="h-8"
            rounded="rounded-lg"
            width="w-full"
          />
        ) : (
          <Input
            errored={!!form.formState.errors.display_name_singular}
            fullWidth
            {...form.register("display_name_singular", {
              required: "This field is required",
            })}
          />
        )}
        {form.formState.errors.display_name_singular && (
          <ErrorHint>
            {form.formState.errors.display_name_singular?.message}
          </ErrorHint>
        )}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-y-2">
        <div className="text-gray-500 font-inter-normal-12px">
          Display Name Plural
        </div>
        {isLoading ? (
          <SkeletonPlaceholder
            height="h-8"
            rounded="rounded-lg"
            width="w-full"
          />
        ) : (
          <Input
            errored={!!form.formState.errors.display_name_plural}
            fullWidth
            {...form.register("display_name_plural", {
              required: "This field is required",
            })}
          />
        )}
        {form.formState.errors.display_name_plural && (
          <ErrorHint>
            {form.formState.errors.display_name_plural?.message}
          </ErrorHint>
        )}
      </div>
    </div>
  );
};
